import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { SharjahPoliceEmblem } from '../SharjahPoliceEmblem';
import { UserIcon, BikeIcon, CheckCircle2Icon, Building2Icon, UserXIcon, XCircleIcon, ShieldCheckIcon, PowerIcon } from 'lucide-react';
const PAIR2_LOGO = 'https://cdn.magicpatterns.com/uploads/51QRCG1WJpxN6EwND9yhQF/Pair2_(2).svg';
export function FleetRestrictionSlide() {
  const reduce = useReducedMotion();
  const base = reduce ? {
    duration: 0
  } : undefined;
  return <section dir="rtl" className="relative w-screen h-screen overflow-hidden flex flex-col" style={{
    background: '#EDF1F4'
  }} data-id="element-309">
      {/* ===== BACKGROUND ===== */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden data-id="element-310">
        <div className="absolute -top-40 -right-40 w-[60vmax] h-[60vmax] rounded-full blur-3xl" style={{
        background: 'radial-gradient(circle, rgba(47,169,156,0.08) 0%, transparent 70%)'
      }} data-id="element-311" />
        <div className="absolute -bottom-40 -left-40 w-[50vmax] h-[50vmax] rounded-full blur-3xl" style={{
        background: 'radial-gradient(circle, rgba(47,169,156,0.05) 0%, transparent 70%)'
      }} data-id="element-312" />
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h40v40H0V0zm1 1h38v38H1V1z' fill='%230B1328' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`
      }} data-id="element-313" />
      </div>

      {/* ===== TOP BAR ===== */}
      <motion.div initial={{
      opacity: 0,
      y: -14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={base ?? {
      duration: 0.9,
      delay: 0.15,
      ease: [0.22, 1, 0.36, 1]
    }} className="relative z-20 flex items-center justify-between pt-[clamp(1.5rem,3.5vh,3rem)] px-[clamp(1.75rem,4.5vw,4.5rem)] shrink-0" data-id="element-314">
        <SharjahPoliceEmblem variant="light" data-id="element-315" />
        <img src={PAIR2_LOGO} alt="Pair²" className="h-[clamp(2.5rem,5vh,3.5rem)] w-auto" style={{
        filter: 'drop-shadow(0 4px 18px rgba(47,169,156,0.28))'
      }} draggable={false} data-id="element-316" />
      </motion.div>

      {/* ===== MAIN CONTENT ===== */}
      <div className="relative z-20 flex-1 flex flex-col justify-center px-[clamp(2rem,5vw,6rem)] pb-[clamp(4rem,8vh,6rem)]" data-id="element-317">
        {/* Title */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={base ?? {
        duration: 0.8,
        delay: 0.2
      }} className="text-center mb-[clamp(2rem,5vh,4rem)]" data-id="element-318">
          <h2 className="font-arabic-display font-black leading-[1.15]" style={{
          color: '#0B1328',
          fontSize: 'clamp(1.8rem, 3vw, 2.5rem)'
        }} data-id="element-319">
            ضبط النطاق التشغيلي
          </h2>
          <div className="mx-auto mt-3 h-[2.5px] rounded-full" style={{
          width: 'clamp(3rem, 5vw, 4rem)',
          background: '#2FA99C',
          opacity: 0.45
        }} data-id="element-320" />
        </motion.div>

        {/* 3 Horizontal Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full max-w-7xl mx-auto" data-id="element-321">
          {/* Card 1: Connection */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={base ?? {
          duration: 0.8,
          delay: 0.4
        }} className="bg-white/80 backdrop-blur-md rounded-3xl p-8 border border-[#2FA99C]/10 shadow-xl shadow-[#2FA99C]/5 flex flex-col items-center text-center" data-id="element-322">
            <div className="w-full h-40 relative flex items-center justify-between px-4 mb-6" data-id="element-323">
              {/* Driver */}
              <div className="relative z-10 w-20 h-20 rounded-2xl bg-[#2FA99C]/10 flex items-center justify-center border border-[#2FA99C]/20 shadow-sm" data-id="element-324">
                <UserIcon className="w-10 h-10 text-[#2FA99C]" data-id="element-325" />
              </div>

              {/* Connection Line */}
              <div className="absolute left-20 right-20 h-1 bg-[#2FA99C]/30 top-1/2 -translate-y-1/2 z-0" data-id="element-326">
                <motion.div initial={{
                scaleX: 0
              }} animate={{
                scaleX: 1
              }} transition={{
                duration: 1,
                delay: 1
              }} className="w-full h-full bg-[#2FA99C] origin-right" data-id="element-327" />
              </div>

              {/* Verified Badge */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 bg-white rounded-full p-1 shadow-md text-[#2FA99C]" data-id="element-328">
                <CheckCircle2Icon className="w-8 h-8" data-id="element-329" />
              </div>

              {/* Motorcycle */}
              <div className="relative z-10 w-20 h-20 rounded-2xl bg-[#2FA99C]/10 flex items-center justify-center border border-[#2FA99C]/20 shadow-sm" data-id="element-330">
                <BikeIcon className="w-10 h-10 text-[#2FA99C]" data-id="element-331" />
              </div>
            </div>
            <h3 className="font-arabic-display font-bold text-[#0B1328] text-xl mb-2" data-id="element-332">
              ربط كل مركبة بسائق محدد
            </h3>
            <p className="font-arabic-body text-[#6E7A8F] text-sm leading-relaxed" data-id="element-333">
              توثيق الربط بين السائق والمركبة لضمان المسؤولية الكاملة.
            </p>
          </motion.div>

          {/* Card 2: Boundary */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={base ?? {
          duration: 0.8,
          delay: 0.5
        }} className="bg-white/80 backdrop-blur-md rounded-3xl p-8 border border-[#2FA99C]/10 shadow-xl shadow-[#2FA99C]/5 flex flex-col items-center text-center" data-id="element-334">
            <div className="w-full h-40 relative flex items-center justify-center mb-6" data-id="element-335">
              <div className="absolute inset-0 border-2 border-dashed border-[#2FA99C]/40 rounded-3xl bg-[#2FA99C]/5 flex flex-col items-center justify-center p-4" data-id="element-336">
                <div className="absolute top-3 right-4 flex items-center gap-2 text-[#2FA99C]/60" data-id="element-337">
                  <span className="font-arabic-body text-xs font-bold" data-id="element-338">
                    جهة العمل
                  </span>
                  <Building2Icon className="w-5 h-5" data-id="element-339" />
                </div>

                <div className="flex items-center justify-center gap-4 mt-4" data-id="element-340">
                  <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center text-[#2FA99C] shadow-md border border-[#2FA99C]/10" data-id="element-341">
                    <UserIcon className="w-7 h-7" data-id="element-342" />
                  </div>
                  <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center text-[#2FA99C] shadow-md border border-[#2FA99C]/10" data-id="element-343">
                    <BikeIcon className="w-7 h-7" data-id="element-344" />
                  </div>
                  <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center text-[#2FA99C] shadow-md border border-[#2FA99C]/10 opacity-60" data-id="element-345">
                    <BikeIcon className="w-7 h-7" data-id="element-346" />
                  </div>
                </div>
              </div>
            </div>
            <h3 className="font-arabic-display font-bold text-[#0B1328] text-xl mb-2" data-id="element-347">
              السائق ضمن نطاق جهة واحدة
            </h3>
            <p className="font-arabic-body text-[#6E7A8F] text-sm leading-relaxed" data-id="element-348">
              حصر صلاحيات السائق للعمل فقط ضمن أسطول الجهة التابع لها.
            </p>
          </motion.div>

          {/* Card 3: Ignition Control */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={base ?? {
          duration: 0.8,
          delay: 0.6
        }} className="bg-white/80 backdrop-blur-md rounded-3xl p-8 border border-[#2FA99C]/10 shadow-xl shadow-[#2FA99C]/5 flex flex-col items-center text-center" data-id="element-349">
            <div className="w-full h-40 flex flex-col justify-center gap-4 mb-6" data-id="element-350">
              {/* Allowed State */}
              <div className="flex items-center justify-between bg-[#10B981]/10 px-5 py-3 rounded-2xl border border-[#10B981]/20" data-id="element-351">
                <div className="flex items-center gap-3 text-[#10B981]" data-id="element-352">
                  <UserIcon className="w-6 h-6" data-id="element-353" />
                  <span className="font-bold text-lg" data-id="element-354">+</span>
                  <BikeIcon className="w-6 h-6" data-id="element-355" />
                </div>
                <div className="flex items-center gap-3" data-id="element-356">
                  <PowerIcon className="w-6 h-6 text-[#10B981]" data-id="element-357" />
                  <CheckCircle2Icon className="w-6 h-6 text-[#10B981]" data-id="element-358" />
                </div>
              </div>

              {/* Blocked State */}
              <div className="flex items-center justify-between bg-[#EF4444]/10 px-5 py-3 rounded-2xl border border-[#EF4444]/20" data-id="element-359">
                <div className="flex items-center gap-3 text-[#EF4444]" data-id="element-360">
                  <UserXIcon className="w-6 h-6" data-id="element-361" />
                  <span className="font-bold text-lg" data-id="element-362">+</span>
                  <BikeIcon className="w-6 h-6" data-id="element-363" />
                </div>
                <div className="flex items-center gap-3" data-id="element-364">
                  <PowerIcon className="w-6 h-6 text-[#EF4444] opacity-50" data-id="element-365" />
                  <XCircleIcon className="w-6 h-6 text-[#EF4444]" data-id="element-366" />
                </div>
              </div>
            </div>
            <h3 className="font-arabic-display font-bold text-[#0B1328] text-xl mb-2" data-id="element-367">
              تشغيل المركبة مرتبط بالسائق
            </h3>
            <p className="font-arabic-body text-[#6E7A8F] text-sm leading-relaxed" data-id="element-368">
              منع تشغيل المحرك إلا في حال وجود السائق الموثق والمعتمد.
            </p>
          </motion.div>
        </div>
      </div>

      {/* ===== BOTTOM STATEMENT ===== */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={base ?? {
      duration: 0.8,
      delay: 1.2
    }} className="absolute bottom-[clamp(1.5rem,3vh,2.5rem)] left-0 right-0 flex justify-center pointer-events-none z-20" data-id="element-369">
        <div className="bg-white/80 backdrop-blur-md px-8 py-3 rounded-full border border-[#2FA99C]/10 shadow-sm" data-id="element-370">
          <p className="font-arabic-body font-bold text-[#0B1328] text-center text-sm md:text-base" data-id="element-371">
            لا يمكن تشغيل أي مركبة دون ربطها بسائق موثق ضمن الجهة التابعة لها
          </p>
        </div>
      </motion.div>
    </section>;
}